import { GLYPH_HEIGHT } from './constants.js';
import { OWL_PRICE_ANCHOR_RGBS } from './generated/owl_price_anchor_colors.js';
function clamp01(x) {
    if (x <= 0)
        return 0;
    if (x >= 1)
        return 1;
    return x;
}
function channelCount(image) {
    return image.channels ?? 4;
}
function strideOf(image) {
    return image.stride ?? image.width * channelCount(image);
}
function pixelOffset(image, x, y) {
    return y * strideOf(image) + x * channelCount(image);
}
function rgbClose(r, g, b, target, tolerance) {
    return Math.abs(r - target[0]) <= tolerance
        && Math.abs(g - target[1]) <= tolerance
        && Math.abs(b - target[2]) <= tolerance;
}
function colorOnWhiteEvidence(r, g, b, ink, residualMax = 72) {
    const vr = ink[0] - 255;
    const vg = ink[1] - 255;
    const vb = ink[2] - 255;
    const denom = vr * vr + vg * vg + vb * vb;
    if (denom <= 1e-9)
        return 0;
    const ur = r - 255;
    const ug = g - 255;
    const ub = b - 255;
    const alpha = clamp01((ur * vr + ug * vg + ub * vb) / denom);
    const er = 255 + alpha * vr;
    const eg = 255 + alpha * vg;
    const eb = 255 + alpha * vb;
    const dr = r - er;
    const dg = g - eg;
    const db = b - eb;
    const residualSq = dr * dr + dg * dg + db * db;
    const penalty = clamp01(1 - residualSq / (residualMax * residualMax));
    return alpha * penalty;
}
export const BLACK_TEXT_PROFILE = {
    name: 'black',
    anchorRgb: [0, 0, 0],
    evidencePixel: (r, g, b) => colorOnWhiteEvidence(r, g, b, [0, 0, 0], 36),
};
export const RED_TEXT_PROFILE = {
    name: 'red',
    anchorRgb: [255, 34, 34],
    anchorRgbs: [[255, 34, 34], [238, 0, 17]],
    evidencePixel: (r, g, b) => Math.max(colorOnWhiteEvidence(r, g, b, [255, 34, 34], 50), colorOnWhiteEvidence(r, g, b, [238, 0, 17], 54)),
};
function owlPriceEvidence(r, g, b) {
    let best = 0;
    for (const color of OWL_PRICE_ANCHOR_RGBS) {
        best = Math.max(best, colorOnWhiteEvidence(r, g, b, color, 72));
    }
    return best;
}
/** Generated palette of every fully-inked Owl price color. */
export const OWL_PRICE_TEXT_PROFILE = {
    name: 'owl-price',
    anchorRgb: OWL_PRICE_ANCHOR_RGBS[0],
    anchorRgbs: OWL_PRICE_ANCHOR_RGBS,
    evidencePixel: owlPriceEvidence,
};
export function textInkProfile(profile = 'black') {
    if (typeof profile !== 'string')
        return profile;
    switch (profile) {
        case 'black': return BLACK_TEXT_PROFILE;
        case 'red': return RED_TEXT_PROFILE;
        case 'owl-price': return OWL_PRICE_TEXT_PROFILE;
    }
}
export function isAnchorPixel(profile, r, g, b) {
    const tolerance = profile.anchorTolerance ?? 0;
    const anchors = profile.anchorRgbs ?? [profile.anchorRgb];
    return anchors.some((target) => rgbClose(r, g, b, target, tolerance));
}
/** Convert a 12px-tall line image into anchor columns and soft shape evidence.
 *
 * The bundled atlas comes from CIDER's MapleLegends glyph templates. Candidate
 * generation still uses exact fully-inked anchor columns, while reranking uses
 * soft shape evidence. For simple light-background UI text this is enough; callers that need
 * more complex backgrounds can provide a custom TextInkProfile.
 */
export function buildLineAnchors(image, options = {}) {
    if (image.height < GLYPH_HEIGHT) {
        throw new Error(`line image height ${image.height} is less than ${GLYPH_HEIGHT}`);
    }
    if (image.data.length < strideOf(image) * (image.height - 1) + image.width * channelCount(image)) {
        throw new Error('image data is smaller than width/height/channels require');
    }
    const profile = textInkProfile(options.profile);
    const bboxPad = options.bboxPad ?? 3;
    const endPad = options.endPad ?? 3;
    const endEstimateBasis = options.endEstimateBasis ?? 'anchor';
    const inkThreshold = options.inkEvidenceThreshold ?? 0.08;
    const anchorEvidenceThreshold = options.anchorEvidenceThreshold;
    const width = image.width;
    const columns = new Array(width).fill(0);
    const evidence = new Float32Array(GLYPH_HEIGHT * width);
    const nonWhiteMask = new Uint8Array(GLYPH_HEIGHT * width);
    const nonWhiteColumns = new Uint16Array(width);
    let exactMinX = Number.POSITIVE_INFINITY;
    let exactMaxX = -1;
    let inkMinX = Number.POSITIVE_INFINITY;
    let inkMaxX = -1;
    for (let y = 0; y < GLYPH_HEIGHT; y++) {
        for (let x = 0; x < width; x++) {
            const i = pixelOffset(image, x, y);
            const r = image.data[i] ?? 0;
            const g = image.data[i + 1] ?? 0;
            const b = image.data[i + 2] ?? 0;
            const pixelIndex = y * width + x;
            const ev = profile.evidencePixel(r, g, b);
            evidence[pixelIndex] = ev;
            if (r !== 255 || g !== 255 || b !== 255) {
                nonWhiteMask[pixelIndex] = 1;
                nonWhiteColumns[x] = (nonWhiteColumns[x] ?? 0) | (1 << y);
            }
            if (ev >= inkThreshold) {
                if (x < inkMinX)
                    inkMinX = x;
                if (x > inkMaxX)
                    inkMaxX = x;
            }
            const anchor = anchorEvidenceThreshold === undefined
                ? isAnchorPixel(profile, r, g, b)
                : ev >= anchorEvidenceThreshold;
            if (anchor) {
                columns[x] = (columns[x] ?? 0) | (1 << y);
                if (x < exactMinX)
                    exactMinX = x;
                if (x > exactMaxX)
                    exactMaxX = x;
            }
        }
    }
    const hasInk = inkMaxX >= 0;
    const hasExact = exactMaxX >= 0;
    const rawX0 = hasInk ? inkMinX : (hasExact ? exactMinX : 0);
    const rawX1 = hasInk ? inkMaxX + 1 : (hasExact ? exactMaxX + 1 : 0);
    const evidenceX0 = Math.max(0, rawX0 - bboxPad);
    const evidenceX1 = Math.min(width, rawX1 + bboxPad);
    // Zero out evidence outside the active text band so trailing UI lines do not
    // accidentally influence fit scoring when a caller passes a wide cell crop.
    if (rawX1 > rawX0) {
        for (let y = 0; y < GLYPH_HEIGHT; y++) {
            const row = y * width;
            for (let x = 0; x < evidenceX0; x++)
                evidence[row + x] = 0;
            for (let x = evidenceX1; x < width; x++)
                evidence[row + x] = 0;
        }
    }
    else {
        evidence.fill(0);
    }
    return {
        width,
        height: GLYPH_HEIGHT,
        columns,
        anchorString: String.fromCharCode(...columns),
        evidence,
        nonWhiteMask,
        nonWhiteColumns,
        evidenceX0,
        evidenceX1,
        exactX0: hasExact ? exactMinX : 0,
        exactX1: hasExact ? exactMaxX + 1 : 0,
        inkX0: hasInk ? inkMinX : 0,
        inkX1: hasInk ? inkMaxX + 1 : 0,
        estimatedStartX: hasInk ? inkMinX : (hasExact ? exactMinX : 0),
        estimatedEndX: Math.min(width, ((endEstimateBasis === 'ink' && hasInk) || !hasExact ? inkMaxX : exactMaxX) + 1 + endPad),
        profile,
    };
}
export function evidenceAt(line, x, y) {
    if (y < 0 || y >= GLYPH_HEIGHT || x < 0 || x >= line.width)
        return 0;
    if (line.evidenceX1 > line.evidenceX0 && (x < line.evidenceX0 || x >= line.evidenceX1))
        return 0;
    return line.evidence[y * line.width + x] ?? 0;
}
export function nonWhiteAt(line, x, y) {
    if (y < 0 || y >= GLYPH_HEIGHT || x < 0 || x >= line.width)
        return false;
    return ((line.nonWhiteColumns[x] ?? 0) & (1 << y)) !== 0;
}
//# sourceMappingURL=preprocess.js.map